import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiClient } from './client';
import { isAuthBypassEnabled } from '@/auth/authSettings';
import { msalInstance } from '@/auth/msalConfig';
import type { DirectoryUser, UserAccess } from '@/types/access';

/**
 * Fetch the access/authorization profile of the currently signed-in user.
 * @returns The current user's access assignment.
 */
export async function getCurrentAccess(): Promise<UserAccess> {
  const { data } = await apiClient.get<UserAccess>('/api/Access/me');
  return data;
}

/**
 * List all known access assignments (admin-only management view).
 * @returns All user access assignments.
 */
export async function getAccessAssignments(): Promise<UserAccess[]> {
  const { data } = await apiClient.get<UserAccess[]>('/api/Access/assignments');
  return data;
}

/**
 * Create or update the access assignment for a user.
 * @param value - The access assignment to persist; `userObjectId` identifies the user.
 * @returns The saved access assignment as returned by the API.
 */
export async function updateAccessAssignment(value: UserAccess): Promise<UserAccess> {
  const { data } = await apiClient.put<UserAccess>(
    `/api/Access/assignments/${encodeURIComponent(value.userObjectId)}`,
    value,
  );
  return data;
}

/**
 * List directory users belonging to the configured admin AAD group.
 * Falls back to a hardcoded default group id if no env override is set.
 * @returns Directory users in the admin group, sorted by display name.
 */
export async function getAdminDirectoryUsers(): Promise<DirectoryUser[]> {
  const groupId = import.meta.env.VITE_AAD_ADMIN_GROUP_ID?.trim()
    || '7f4de33c-09e2-4889-9958-fe96b393846e';
  return getDirectoryGroupUsers(groupId);
}

/**
 * List directory users belonging to the configured employee AAD group.
 * Falls back to a hardcoded default group id if no env override is set.
 * @returns Directory users in the employee group, sorted by display name.
 */
export async function getEmployeeDirectoryUsers(): Promise<DirectoryUser[]> {
  const groupId = import.meta.env.VITE_AAD_EMPLOYEE_GROUP_ID?.trim()
    || '892f74bb-f8c9-408a-827a-d214a90e6920';
  return getDirectoryGroupUsers(groupId);
}

/**
 * Fetch all transitive user members of a Microsoft Entra ID (AAD) group via Microsoft Graph,
 * paging through `@odata.nextLink` results.
 * @param groupId - Object id of the AAD group to enumerate.
 * @returns Directory users in the group, sorted alphabetically (Dutch locale).
 * @throws If no signed-in account is available, or Graph returns a non-OK response
 * (e.g. missing delegated permissions/admin consent).
 */
async function getDirectoryGroupUsers(groupId: string): Promise<DirectoryUser[]> {
  // Auth bypass (local/dev) has no real AAD token, so directory lookups are skipped.
  if (isAuthBypassEnabled) return [];
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new Error('Geen aangemelde gebruiker gevonden.');

  const request = { scopes: ['GroupMember.Read.All', 'User.ReadBasic.All'], account };
  let token: string;
  try {
    token = (await msalInstance.acquireTokenSilent(request)).accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      token = (await msalInstance.acquireTokenPopup(request)).accessToken;
    } else {
      throw error;
    }
  }

  const users: DirectoryUser[] = [];
  let nextUrl: string | undefined = `https://graph.microsoft.com/v1.0/groups/${encodeURIComponent(groupId)}/transitiveMembers/microsoft.graph.user?$count=true&$select=id,displayName,userPrincipalName,mail&$top=999`;
  while (nextUrl) {
    const response = await fetch(nextUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        ConsistencyLevel: 'eventual',
      },
    });
    if (!response.ok) {
      throw new Error(response.status === 403
        ? 'Microsoft Graph-toegang ontbreekt. Geef de SPA delegated GroupMember.Read.All en User.ReadBasic.All, en verleen admin consent.'
        : `Microsoft Graph kon de groepsleden niet laden (${response.status}).`);
    }
    const page = await response.json() as { value: DirectoryUser[]; '@odata.nextLink'?: string };
    users.push(...page.value);
    nextUrl = page['@odata.nextLink'];
  }
  return users.sort((a, b) => a.displayName.localeCompare(b.displayName, 'nl'));
}
